/**
 * المرحلة 1.35 — «مرتجع» on a voucher, in one window: tick the services
 * coming back (some of a line's days, hours or sessions, or all of it), tap
 * a ready reason or write one, «سجّل المرتجع». The voucher is never edited:
 * the refund is added to it, cancels what was still owed first, and only the
 * rest goes back in cash (domain/refund.js previews it; the server plans it
 * again: Backend/routes/receipts.js POST /:id/refund).
 *
 * `receipt` is the voucher as the server presents it, with its money.
 */

import { useMemo, useState } from 'react';
import { Button, Icon, Input, Modal, Textarea } from '../../components/common';
import { receiptsApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { useI18n } from '../../i18n/I18nProvider';
import { formatDateTime, formatMoney } from '../../domain/locale';
import { MIN_REASON, planRefund, refundable } from '../../domain/refund';

const READY_REASONS = ['cancelled', 'notDone', 'earlyOut', 'mistake'];

export function RefundDialog({ receipt, open, onClose, onDone }) {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const lines = useMemo(() => (receipt ? refundable(receipt) : []), [receipt]);
  const [picked, setPicked] = useState({});
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const asked = Object.entries(picked)
    .filter(([, v]) => v && v.on)
    .map(([index, v]) => ({ index: Number(index), quantity: v.quantity === '' ? undefined : Number(String(v.quantity).replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))) }));
  // The money before the reason is typed: the plan with a stand-in reason.
  const preview = receipt && asked.length ? planRefund(receipt, { lines: asked, reason: 'preview' }) : null;
  const left = lines.filter((l) => l.leftAmount > 0);
  const history = receipt && Array.isArray(receipt.refunds) ? receipt.refunds : [];
  const ready = preview && !preview.error && reason.trim().length >= MIN_REASON;

  const toggle = (line) =>
    setPicked((p) => ({ ...p, [line.index]: p[line.index] && p[line.index].on ? { ...p[line.index], on: false } : { on: true, quantity: String(line.leftQuantity) } }));
  const all = () => setPicked(Object.fromEntries(left.map((l) => [l.index, { on: true, quantity: String(l.leftQuantity) }])));

  const close = () => {
    setPicked({});
    setReason('');
    onClose();
  };

  const save = async () => {
    setBusy(true);
    try {
      const out = await receiptsApi.refund(receipt.id, { reason: reason.trim(), lines: asked });
      success(out.full ? t('refund.full') : t('refund.done', { amount: formatMoney(out.refund.amount) }));
      setPicked({});
      setReason('');
      onDone(out.receipt, out.refund);
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!receipt) return null;
  return (
    <Modal
      open={open}
      onClose={close}
      title={t('refund.title', { number: receipt.number })}
      icon="undo-2"
      size="lg"
      footer={
        <div className="rf-foot">
          {preview && !preview.error && (
            <div className="rf-sum" data-testid="refund-sum">
              <span>
                {t('refund.amount')} <b>{formatMoney(preview.refund.amount)}</b>
              </span>
              {preview.refund.absorbed > 0 && (
                <span>
                  {t('refund.absorbed')} <b>{formatMoney(preview.refund.absorbed)}</b>
                </span>
              )}
              <span className="rf-cash">
                {t('refund.cash')} <b>{formatMoney(preview.refund.cash)}</b>
              </span>
            </div>
          )}
          {preview && preview.error && <span className="rf-error">{preview.error}</span>}
          <div className="rf-buttons">
            <Button variant="ghost" onClick={close} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" icon="undo-2" loading={busy} disabled={!ready} onClick={save} data-testid="refund-save">
              {t('refund.save')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="rf" data-testid="refund-dialog">
        <p className="rf-intro">{t('refund.intro')}</p>
        {left.length === 0 ? (
          <p className="rf-none">{t('refund.nothing')}</p>
        ) : (
          <>
            <div className="rf-head">
              <span>{t('refund.pick')}</span>
              <Button variant="subtle" icon="check-check" onClick={all} data-testid="refund-all">
                {t('refund.all')}
              </Button>
            </div>
            <ul className="rf-lines">
              {left.map((line) => {
                const on = Boolean(picked[line.index] && picked[line.index].on);
                return (
                  <li key={line.index} className={`rf-line${on ? ' is-on' : ''}`}>
                    <label className="rf-check">
                      <input type="checkbox" checked={on} onChange={() => toggle(line)} data-testid="refund-line" />
                      <span className="rf-name">{line.label}</span>
                    </label>
                    {on && line.leftQuantity > 1 && (
                      <span className="rf-qty">
                        <Input
                          aria-label={t('refund.back')}
                          value={picked[line.index].quantity}
                          onChange={(e) => setPicked((p) => ({ ...p, [line.index]: { ...p[line.index], quantity: e.target.value } }))}
                          inputMode="decimal"
                          dir="ltr"
                        />
                        <small>
                          {t('refund.of', { n: line.leftQuantity })} {t(`priceList.units.${line.unit}`)}
                        </small>
                      </span>
                    )}
                    <b className="rf-left">{formatMoney(line.leftAmount)}</b>
                  </li>
                );
              })}
            </ul>
            <div className="rf-reason">
              <span className="rf-label">{t('refund.reason')}</span>
              <div className="rf-chips" role="group" aria-label={t('refund.ready')}>
                {READY_REASONS.map((key) => (
                  <button key={key} type="button" className={`qk-chip${reason === t(`refund.reasons.${key}`) ? ' is-on' : ''}`} onClick={() => setReason(t(`refund.reasons.${key}`))}>
                    {t(`refund.reasons.${key}`)}
                  </button>
                ))}
              </div>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('refund.reasonHint')} data-testid="refund-reason" />
            </div>
          </>
        )}
        {history.length > 0 && (
          <div className="rf-history">
            <span className="rf-label">
              <Icon name="history" size={14} /> {t('refund.history')}
            </span>
            <ul>
              {history.map((x) => (
                <li key={x.no}>
                  <b>{x.amount !== undefined ? formatMoney(x.amount) : ''}</b> — {(x.lines || []).map((l) => l.label).join('، ')}
                  <small>
                    {x.reason} · {t('refund.by', { name: x.byName, at: formatDateTime(x.at) })}
                  </small>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default RefundDialog;
